import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaCalendar,
  FaClock,
  FaEye,
  FaUser,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaImage,
  FaVideo,
  FaTag,
  FaGlobe,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaUsers,
  FaChartBar
} from "react-icons/fa";

const FreeApprovedAds = () => {
  const [ads, setAds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // Add these state variables for the preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [previewType, setPreviewType] = useState("");
  // Pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Function to handle media click
  const handleMediaClick = (mediaUrl, mediaType) => {
    setPreviewMedia(mediaUrl);
    setPreviewType(mediaType);
    setShowPreview(true);
  };

  // Function to close the preview
  const closePreview = () => {
    setShowPreview(false);
    setPreviewMedia(null);
  };

  // Media preview modal component
  const MediaPreviewModal = () => {
    if (!showPreview) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
        <div className="relative max-w-4xl max-h-[90vh] overflow-auto p-4">
          <button
            onClick={closePreview}
            className="absolute top-2 right-2 bg-white rounded-full p-2 text-gray-800 hover:text-gray-900 z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {previewType.toLowerCase() === "video" ? (
            <video
              src={previewMedia}
              controls
              autoPlay
              className="max-h-[80vh] max-w-full"
            />
          ) : (
            <img
              src={previewMedia}
              alt="Preview"
              className="max-h-[80vh] max-w-full"
            />
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/accepted/reporters/free/ad`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Response of Free Approved Ads:", response);
        // Ensure ads is always an array
        const adsData =
          Array.isArray(response.data.data) && response.data.data.length > 0
            ? response.data.data
            : Array.isArray(response.data) && response.data.length > 0
              ? response.data
              : [];
        setAds(adsData);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-green-500 animate-spin"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-emerald-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-teal-500 animate-spin" style={{ animationDuration: '2s' }}></div>
          </div>
          <p className="text-lg font-semibold text-gray-700">Loading Free Approved Ads...</p>
          <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
        </div>
      </div>
    );

  // Filter logic for search bar
  const filteredAds = ads.filter((ad) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    // If the search term matches an IINSAF ID or INF ID format, show only ads with that exact ID
    if (/^(iinsaf|inf)\d+$/i.test(searchTerm.trim())) {
      return ad.acceptedReporters && ad.acceptedReporters.some((reporter) => {
        return reporter.iinsafId && reporter.iinsafId.toString().toLowerCase() === term;
      });
    }
    // Otherwise, do a general search
    if (
      (ad.adId && ad.adId.toString().toLowerCase().includes(term)) ||
      (ad._id && ad._id.toString().toLowerCase().includes(term)) ||
      (ad.adType && ad.adType.toLowerCase().includes(term)) ||
      (Array.isArray(ad.state) ? ad.state.some((s) => s && s.toLowerCase().includes(term)) : (ad.state && ad.state.toLowerCase().includes(term))) ||
      (Array.isArray(ad.city) ? ad.city.some((c) => c && c.toLowerCase().includes(term)) : (ad.city && ad.city.toLowerCase().includes(term))) ||
      (ad.description && ad.description.toLowerCase().includes(term))
    ) {
      return true;
    }
    if (ad.acceptedReporters && ad.acceptedReporters.some((reporter) => {
      return (
        (reporter.iinsafId && reporter.iinsafId.toString().toLowerCase().includes(term)) ||
        (reporter.reporterId && reporter.reporterId.toString().toLowerCase().includes(term)) ||
        (reporter.channelName && reporter.channelName.toLowerCase().includes(term)) ||
        (reporter.duration && reporter.duration.toString().toLowerCase().includes(term)) ||
        (reporter.acceptedAt && new Date(reporter.acceptedAt).toLocaleDateString("en-GB").toLowerCase().includes(term))
      );
    })) {
      return true;
    }
    return false;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentAds = filteredAds.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      <MediaPreviewModal />

      {/* Premium Header */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 rounded-[2.5rem] shadow-2xl p-6 sm:p-14 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -mr-40 -mt-40" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-400/10 rounded-full blur-2xl -ml-32 -mb-32" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="flex items-center gap-4 sm:gap-8">
              <div className="p-4 sm:p-6 bg-white/15 rounded-[2rem] backdrop-blur-md shadow-inner border border-white/10">
                <FaCheckCircle className="text-2xl sm:text-5xl text-emerald-200" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-5xl font-black tracking-tight leading-none uppercase">Accepted Ads</h1>
                <p className="text-emerald-100 mt-2 text-[10px] sm:text-sm font-black uppercase tracking-[0.2em] opacity-80">Campaign Archives • Active Verifications</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-4">
              {[
                { label: 'Pending', path: '/admin/freependingads', icon: FaClock },
                { label: 'Running', path: '/admin/freerunningads', icon: FaPlay },
                { label: 'Completed', path: '/admin/freecompletedads', icon: FaCheckCircle }
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => navigate(btn.path)}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 px-4 sm:px-6 py-3 rounded-2xl transition-all font-black text-[10px] sm:text-xs uppercase tracking-widest backdrop-blur-sm flex items-center gap-2 active:scale-95"
                >
                  <btn.icon /> {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 mb-8">
        <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-4 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative flex-1">
            <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              className="w-full pl-14 pr-6 py-4 sm:py-5 bg-slate-50 border-none rounded-[1.5rem] text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 transition-all shadow-inner"
              placeholder="FILTER BY AD ID, USER OR LOCATION..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-2xl border border-emerald-100 shrink-0">
            <FaChartBar className="text-emerald-500" />
            <span className="text-xs font-black text-emerald-700 uppercase tracking-widest">{filteredAds.length} RECORDS FOUND</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {filteredAds.length === 0 ? (
          <div className="bg-white rounded-[3rem] shadow-xl p-20 text-center border border-slate-100">
            <FaSearch className="text-5xl text-slate-100 mx-auto mb-6" />
            <p className="font-black text-slate-400 uppercase tracking-widest">No matching advertisements found</p>
          </div>
        ) : (
          <div className="space-y-10 sm:space-y-14">
            {[...currentAds]
              .sort((a, b) => {
                const aLatest = a.acceptedReporters?.length > 0 ? Math.max(...a.acceptedReporters.map(r => r.acceptedAt ? new Date(r.acceptedAt).getTime() : 0)) : 0;
                const bLatest = b.acceptedReporters?.length > 0 ? Math.max(...b.acceptedReporters.map(r => r.acceptedAt ? new Date(r.acceptedAt).getTime() : 0)) : 0;
                return bLatest - aLatest;
              })
              .map((ad) => (
                <div key={ad._id} className="bg-white rounded-[2.5rem] sm:rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden group">
                  {/* Ad Header */}
                  <div className="bg-slate-50/80 px-6 sm:px-12 py-6 sm:py-8 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-[1.5rem] shadow-lg flex items-center justify-center border border-slate-100 shrink-0">
                        <FaTag className="text-emerald-500 text-xl sm:text-2xl" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight uppercase">{ad.adId || ad._id}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Deployment:</span>
                          <span className="text-xs font-black text-emerald-600 tracking-tighter">{ad.acceptedReporters?.length || 0} / {ad.requiredReportersCount}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="hidden sm:block">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 text-right">Deployment Status</p>
                        <div className="flex items-center gap-3">
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                              style={{ width: `${Math.min(100, ((ad.acceptedReporters?.length || 0) / ad.requiredReportersCount) * 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-black text-slate-800">
                            {Math.round(((ad.acceptedReporters?.length || 0) / ad.requiredReportersCount) * 100)}%
                          </span>
                        </div>
                      </div>
                      <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-100">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Category</span>
                        <span className="text-xs font-black text-slate-800 uppercase">{ad.adType || 'Ad'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Deployment Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100">
                      <thead className="bg-slate-50/30">
                        <tr>
                          <th className="px-12 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Operator</th>
                          <th className="px-12 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Geo-Target</th>
                          <th className="px-12 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Verification</th>
                          <th className="px-12 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Payload Proof</th>
                          <th className="px-12 py-5 text-right text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {ad.acceptedReporters?.map((reporter, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-12 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center font-black text-slate-400 text-xs">
                                  {(reporter.iinsafId || reporter.reporterId).charAt(0)}
                                </div>
                                <span className="text-sm font-black text-slate-800 tracking-tight">{reporter.iinsafId || reporter.reporterId}</span>
                              </div>
                            </td>
                            <td className="px-12 py-4">
                              <div className="flex items-center gap-2 text-slate-500">
                                <FaMapMarkerAlt className="text-[10px]" />
                                <span className="text-xs font-bold truncate max-w-[120px]">{Array.isArray(ad.city) ? ad.city.join(', ') : ad.city || 'N/A'}</span>
                              </div>
                            </td>
                            <td className="px-12 py-4">
                              <span className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${reporter.postStatus === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                                {reporter.postStatus}
                              </span>
                            </td>
                            <td className="px-12 py-4">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${reporter.adProof === "true" ? 'bg-emerald-500 shadow-emerald-200 shadow-[0_0_8px]' : 'bg-rose-400'}`} />
                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{reporter.adProof === "true" ? 'Legit' : 'Void'}</span>
                              </div>
                            </td>
                            <td className="px-12 py-4 text-right">
                              <span className="text-[10px] font-bold text-slate-400 uppercase">{reporter.acceptedAt ? new Date(reporter.acceptedAt).toLocaleDateString() : '-'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Deployment Roster */}
                  <div className="lg:hidden divide-y divide-slate-50">
                    {ad.acceptedReporters?.map((reporter, idx) => (
                      <div key={idx} className="p-6 flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center font-black text-slate-400 shrink-0 border border-slate-100">
                              {(reporter.iinsafId || reporter.reporterId).charAt(0)}
                            </div>
                            <span className="text-sm font-black text-slate-900 tracking-tight">{reporter.iinsafId || reporter.reporterId}</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">{reporter.acceptedAt ? new Date(reporter.acceptedAt).toLocaleDateString() : ''}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 text-center">Verification</p>
                            <div className={`text-center text-[10px] font-black uppercase ${reporter.postStatus === 'completed' ? 'text-emerald-600' : 'text-amber-500'}`}>
                              {reporter.postStatus}
                            </div>
                          </div>
                          <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 text-center">Evidence</p>
                            <div className={`text-center text-[10px] font-black uppercase ${reporter.adProof === "true" ? 'text-emerald-600' : 'text-rose-500'}`}>
                              {reporter.adProof === "true" ? 'Received' : 'Pending'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Premium Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-12 mb-8">
          <nav className="inline-flex items-center gap-2 bg-white rounded-[2rem] shadow-2xl p-3 border border-slate-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-emerald-50 hover:text-emerald-700 transition-all active:scale-90"
            >
              <FaArrowLeft />
            </button>
            <div className="flex gap-1.5">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pNum = i + 1;
                return (
                  <button
                    key={pNum}
                    onClick={() => handlePageChange(pNum)}
                    className={`w-12 h-12 rounded-2xl font-black text-sm transition-all active:scale-95 ${currentPage === pNum ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-100' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
                  >
                    {pNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-emerald-50 hover:text-emerald-700 transition-all active:scale-90"
            >
              <FaArrowRight />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default FreeApprovedAds;
